import { useCallback, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, FormControlLabel, Grid, MenuItem, Radio, RadioGroup } from "@mui/material"
import { useAppLocalization } from "../../../../../../../localization/components/AppLocalizationProvider"
import { useFormikContext } from "formik"
import { ApiQueryTable } from "../../../../../../../../core/api/components/ApiQueryTable"
import { StandardMainPanel } from "../../../../../../../../shared/components/forms/StandardMainPanel"
import { BookingTimeType, ClosingTime, ClosingTimeType, CreateRoomResourceForm } from "../../../resourcesTypes"
import { StandardForm } from "../../../../../../../../shared/components/forms/StandardForm"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { StandardButton } from "../../../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { FormikTimePicker } from "../../../../../../../../shared/components/forms/Formik"
import { AddClosingTimeDialog } from "./AddClosingTimeDialog"
import { useColumnFactory } from "../../../../../../../../core/api/useTableColumns"
import { COLUMN_SIZE } from "../../../../../../../../shared/constants/table/config"

const useStyles = makeStyles()((theme) => ({
    tablePaper: {
        boxShadow: "none",
        "& .MuiTableBody-root td[colspan] .MuiTypography-root": {
            maxWidth: "none",
        },
    },
    tableContainer: {
        maxHeight: "100%",
        borderRadius: 0,
        padding: "0",
    },
    tableHeadCell: {
        border: "none",
        color: theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: theme.palette.tailwind.slate[50],
    },
    bookingTimeFields: {
        paddingLeft: "1.875rem",
        paddingTop: ".25rem",
        paddingBottom: ".5rem",
        maxWidth: "32rem",
    },
}))

export function AvailabilityContent() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { setFieldValue, values } = useFormikContext<CreateRoomResourceForm>()
    const [shodAddDialog, setShodAddDialog] = useState(false)

    const periodsClosingTimes = useMemo(
        () => values.closingTimes.filter((i) => i.type === ClosingTimeType.PERIOD),
        [values.closingTimes],
    )

    const columnFactory = useColumnFactory<ClosingTime>()

    const columns = useMemo<MRT_ColumnDef<ClosingTime>[]>(
        () => [
            {
                ...columnFactory.datetimeColumn(
                    {
                        header: l10n.getString("tab-availability-table-start-header"),
                        accessorKey: "dateTimeInterval.from",
                    },
                    (row) => row.dateTimeInterval?.from,
                ),
                size: COLUMN_SIZE.SMM,
                minSize: COLUMN_SIZE.SMM,
                maxSize: COLUMN_SIZE.SMM,
                muiTableHeadCellProps: { align: "left" },
                muiTableBodyCellProps: { align: "left" },
            },
            {
                ...columnFactory.datetimeColumn(
                    {
                        header: l10n.getString("tab-availability-table-end-header"),
                        accessorKey: "dateTimeInterval.to",
                    },
                    (row) => row.dateTimeInterval?.to,
                ),
                size: COLUMN_SIZE.SMM,
                minSize: COLUMN_SIZE.SMM,
                maxSize: COLUMN_SIZE.SMM,
                muiTableHeadCellProps: { align: "left" },
                muiTableBodyCellProps: { align: "left" },
            },
            columnFactory.nameColumn({
                header: l10n.getString("tab-availability-table-reason-header"),
                accessorKey: "reason",
            }),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ closeMenu }: { row: MRT_Row<ClosingTime>; closeMenu: () => void }) => [
            <MenuItem
                key="edit"
                onClick={() => {
                    closeMenu()
                }}
            >
                <LocalizedStrict id="tab-availability-table-edit-action">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key="delete"
                onClick={() => {
                    closeMenu()
                }}
            >
                <LocalizedStrict id="tab-availability-table-delete-action">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [setFieldValue],
    )

    return (
        <StandardForm>
            <LocalizedStrict id="tab-availability-side-daily-booking-times-title" attrs={{ title: true }}>
                <StandardMainPanel variant="collapsible" title="Daily booking hours">
                    <RadioGroup
                        value={values.bookingTimeType}
                        onChange={(event) => setFieldValue("bookingTimeType", event.target.value)}
                    >
                        <LocalizedStrict id="tab-availability-booking-time-option-all-day" attrs={{ label: true }}>
                            <FormControlLabel
                                value={BookingTimeType.AVAILABLE_ALL_DAY}
                                control={<Radio size="small" />}
                                label="Available all day"
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="tab-availability-booking-time-option-specific-hours"
                            attrs={{ label: true }}
                        >
                            <FormControlLabel
                                value={BookingTimeType.ACCEPT_BOOKING_TIME}
                                control={<Radio size="small" />}
                                label="Only available during specific hours"
                            />
                        </LocalizedStrict>
                        {values.bookingTimeType === BookingTimeType.ACCEPT_BOOKING_TIME && (
                            <Grid container direction="row" gap={2} className={classes.bookingTimeFields}>
                                <Grid item sm>
                                    <LocalizedStrict
                                        id="tab-availability-booking-time-from-label"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTimePicker
                                            showMobilePicker={false}
                                            label="From"
                                            name="bookingTime.periodInTime.from"
                                        />
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item sm>
                                    <LocalizedStrict
                                        id="tab-availability-booking-time-to-label"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTimePicker
                                            showMobilePicker={false}
                                            label="To"
                                            name="bookingTime.periodInTime.to"
                                        />
                                    </LocalizedStrict>
                                </Grid>
                            </Grid>
                        )}
                        <LocalizedStrict id="tab-availability-booking-time-option-daily-break" attrs={{ label: true }}>
                            <FormControlLabel
                                value={BookingTimeType.BLOCK_BOOKING_TIME}
                                control={<Radio size="small" />}
                                label="Unavailable during a daily break"
                            />
                        </LocalizedStrict>
                        {values.bookingTimeType === BookingTimeType.BLOCK_BOOKING_TIME && (
                            <Grid container direction="row" gap={2} className={classes.bookingTimeFields}>
                                <Grid item sm>
                                    <LocalizedStrict
                                        id="tab-availability-booking-time-from-label"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTimePicker
                                            showMobilePicker={false}
                                            label="From"
                                            name="closingTimesDaily.periodInTime.from"
                                        />
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item sm>
                                    <LocalizedStrict
                                        id="tab-availability-booking-time-to-label"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTimePicker
                                            showMobilePicker={false}
                                            label="To"
                                            name="closingTimesDaily.periodInTime.to"
                                        />
                                    </LocalizedStrict>
                                </Grid>
                            </Grid>
                        )}
                    </RadioGroup>
                </StandardMainPanel>
            </LocalizedStrict>
            <StandardMainPanel variant="collapsible" title={l10n.getString("tab-availability-closed-periods-title")}>
                <ApiQueryTable
                    isLoading={false}
                    isFetching={false}
                    isError={false}
                    data={periodsClosingTimes}
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    muiTablePaperProps={{ className: classes.tablePaper }}
                    muiTableContainerProps={{ className: classes.tableContainer }}
                    muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                    muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                    renderRowActionMenuItems={renderRowActionMenuItems}
                />
                <Box>
                    <LocalizedStrict id="tab-availability-add-closed-period-action">
                        <StandardButton
                            onClick={() => setShodAddDialog(true)}
                            color="blue"
                            fullWidth={false}
                            variant="contained"
                        >
                            Add unavailable period
                        </StandardButton>
                    </LocalizedStrict>
                    {shodAddDialog && (
                        <AddClosingTimeDialog
                            open={shodAddDialog}
                            onClose={() => setShodAddDialog(false)}
                            onAdd={(newItem) => {
                                setFieldValue("closingTimes", [
                                    ...values.closingTimes,
                                    {
                                        type: ClosingTimeType.PERIOD,
                                        dateTimeInterval: {
                                            from: newItem.start,
                                            to: newItem.end,
                                        },
                                        reason: newItem.reason,
                                    },
                                ])
                                setShodAddDialog(false)
                            }}
                        />
                    )}
                </Box>
            </StandardMainPanel>
        </StandardForm>
    )
}
